/* eslint-disable @typescript-eslint/require-await */
import { action, set } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import type EmployeeModel from 'garaje/models/employee';
import type GenericResourceModel from 'garaje/models/generic-resource';
import type MapFeatureModel from 'garaje/models/map-feature';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import employeesSearcherTask, { type EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import urlBuilder from 'garaje/utils/url-builder';

interface GenericResourceFormArgs {
  feature: MapFeatureModel;
  floorMapFeatures: MapFeatureModel[];
  additionalDetails: {
    employees: EmployeeModel[];
  };
  isDraftMap: boolean;
  removeFeature: () => void;
  back: () => void;
  updateResourceOverview: (action: string, feature: MapFeatureModel) => void;
}

export default class GenericResourceForm extends Component<GenericResourceFormArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare abilities: AbilitiesService;
  @service declare ajax: AjaxFetchService;

  @tracked availableAmenities: GenericResourceModel['amenities'] = [];
  @tracked searchedEmployees: EmployeeModel[] = [];
  @tracked employeeError: string[] = [];
  @tracked nameError: string[] = [];
  @tracked inputElement?: HTMLInputElement;

  get selectedEmployee(): undefined | { id: string; name: string } {
    const employeeId = this.resource?.assignments?.[0]?.employeeId;

    if (!employeeId) {
      return;
    }

    const employee = this.store.peekRecord('employee', employeeId);

    if (!employee) {
      return { id: String(employeeId), name: String(employeeId) };
    }

    return { id: String(employeeId), name: employee.name };
  }

  get hasResourceError(): boolean {
    return !!(this.nameError.length || this.employeeError.length);
  }

  get cannotManageResource(): boolean {
    return false;
  }

  get resource(): GenericResourceModel | null {
    if (!this.args.feature.externalId) return null;
    return this.store.peekRecord('generic-resource', this.args.feature.externalId);
  }

  get selectedAmenities(): GenericResourceModel['amenities'] {
    return this.resource?.amenities || [];
  }

  get employeesToShow(): EmployeeModel[] {
    if (this.searchedEmployees.length) {
      return this.searchedEmployees;
    }
    return this.args.additionalDetails.employees;
  }

  @action
  inserted(): void {
    void this.getAvailableAmenities.perform();
  }

  @action
  inputInserted(element: HTMLInputElement): void {
    this.inputElement = element;
    this.focusInput();
  }

  @action
  focusInput(): void {
    next(() => {
      this.inputElement?.focus();
    });
  }

  setResourceHasError(): void {
    const { feature } = this.args;

    if (feature.isDestroyed) {
      return;
    }

    if (this.hasResourceError) {
      set(feature, 'hasError', true);
      return;
    }

    set(feature, 'hasError', false);
  }

  @action
  validateNameField(): void {
    const { feature } = this.args;
    const changedAttrs = feature.changedAttributes();

    if (isEmpty(feature.name) && (Object.keys(changedAttrs).includes('name') || feature.hasError || !feature.isValid)) {
      this.nameError = ['Please enter a name'];
    } else if (feature.name?.length > 40) {
      this.nameError = [''];
    } else {
      this.nameError = [];
    }
    this.setResourceHasError();
  }

  clearEmployeeErrors(): void {
    this.employeeError = [];
  }

  @action
  toggleEnabled(enabled: boolean): void {
    this.args.feature.enabled = enabled;
  }

  @action
  onResourceNameChange(name: string): void {
    const { feature, updateResourceOverview } = this.args;

    feature.name = name;

    if (this.resource) {
      this.resource.name = name;
    }

    if (updateResourceOverview) {
      updateResourceOverview('update', feature);
    }

    this.validateNameField();
  }

  @action
  setSelectedAmenities(selectedAmenities: GenericResourceModel['amenities']): void {
    if (!this.resource) {
      return;
    }

    this.resource.amenities = selectedAmenities;
  }

  @action
  returnFalseValid(): boolean {
    return false;
  }

  @action
  onEmployeeSelect(option: string | EmployeeModel): void {
    if (option === 'loadMore' || typeof option === 'string') {
      return;
    }

    this.clearEmployeeErrors();

    if (this.resource) {
      this.resource.assignments = [{ employeeId: Number(option.id) }];
    }
  }

  @(employeesSearcherTask({
    filter: {
      deleted: false,
    },
    prefix: true,
  }).restartable())
  _searchEmployeesTask!: EmployeeSearcherTask;

  employeesSearcherTask = task({ restartable: true }, async (term: string) => {
    const extraFilters = {
      locations: this.state.currentLocation.id,
      deleted: false,
    };
    this.searchedEmployees = await this._searchEmployeesTask.perform(term, extraFilters);
  });

  onEmployeeSearchTask = task({ restartable: true }, async (searchTerm: string) => {
    if (searchTerm) {
      await this.employeesSearcherTask.perform(searchTerm);
    }
    const validEmployee = this.searchedEmployees.find(
      (employee) => searchTerm.toLowerCase().trim() === employee.name?.toLowerCase(),
    );

    if (validEmployee) {
      return this.clearEmployeeErrors();
    }

    if (searchTerm === '' && this.resource) {
      this.resource.assignments = [];
    }

    if (searchTerm === '') {
      this.searchedEmployees = [];
      return;
    }

    this.employeeError = ["Employee doesn't exist"];
  });

  getAvailableAmenities = task({ restartable: true }, async () => {
    const resource = this.resource;

    if (!resource) {
      return;
    }

    const url = urlBuilder.rms.amenities(resource.resourceTypeId);

    const response: { data: GenericResourceModel['amenities'] } = await this.ajax.request(url, {
      type: 'GET',
      contentType: 'application/json',
    });

    this.availableAmenities = response.data;
  });

  validateAssignedEmployeeTask = task({ restartable: true }, async () => {
    this.clearEmployeeErrors();

    if (!this?.resource?.assignments?.[0]) {
      return;
    }

    const employeeId = String(this.resource.assignments[0].employeeId);
    const employees = await this.store.query('employee', {
      filter: { deleted: false, id: employeeId, locations: this.state.currentLocation.id },
    });
    const employee = employees.find((employee) => employee.id === employeeId);

    if (!employee) {
      this.resource.assignments = [];
      this.employeeError = ["Employee doesn't exist"];
      this.setResourceHasError();
    }
  });

  onEmployeeFocusOutTask = task({ drop: true }, async () => {
    void this.validateAssignedEmployeeTask.perform();
  });
}
